import { FC, useEffect, useState } from "react"
import { v4 as uuidv4 } from "uuid"
import { FormContainer, useForm, useWatch } from "react-hook-form-mui"
import { Box, Button, CircularProgress, Divider, Stack } from "@mui/material"
import { SnackbarAlert } from "@/core/components"
import { useCreateBulkMutation } from "../../store"
import { OSINT } from "../../interfaces"
import { QueryTypeSelect } from "../formFields/queryTypeSelect"
import { BulkInputField, parseBulkInput } from "../formFields/bulkInputField"

interface IFormData {
  queryType?: OSINT.PayloadType
  bulkInput?: string
}

interface IProps {
  onSuccess?: () => void
}

export const SherlokQueryForm: FC<IProps> = ({ onSuccess }) => {
  const [createBulk, { isLoading, isError, isSuccess, error, reset }] =
    useCreateBulkMutation()

  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [createdCount, setCreatedCount] = useState(0)
  useEffect(() => {
    if (isError || isSuccess) setSnackbarOpen(true)
  }, [isError, isSuccess])

  const formContext = useForm<IFormData>({
    defaultValues: { queryType: "phone", bulkInput: "" },
  })

  const onSubmit = async (data: IFormData) => {
    const parsed = parseBulkInput(data.bulkInput ?? "", data.queryType)
    if (parsed.payloads.length === 0) return

    try {
      const res = await createBulk({
        body: { items: parsed.payloads },
        idempotencyKey: uuidv4(),
      }).unwrap()
      setCreatedCount(res.items?.length ?? parsed.payloads.length)
      formContext.reset({ queryType: data.queryType, bulkInput: "" })
      onSuccess?.()
    } catch {
      // ошибка покажется через snackbar (isError)
    }
  }

  return (
    <FormContainer<IFormData> formContext={formContext} onSuccess={onSubmit}>
      <SnackbarAlert
        isOpen={snackbarOpen}
        onClose={() => {
          setSnackbarOpen(false)
          reset()
        }}
        variant={isError ? "error" : "success"}
        title={isError ? "Не удалось создать запросы" : "Запросы созданы"}
        message={
          isError
            ? typeof error === "object" && error && "data" in error
              ? JSON.stringify((error as { data: unknown }).data)
              : "Произошла ошибка"
            : `Создано запросов: ${createdCount}`
        }
      />
      <Stack spacing={2}>
        <QueryTypeSelect required />
        <BulkInputField required />
        <Divider light />
        <SubmitRow isLoading={isLoading} />
      </Stack>
    </FormContainer>
  )
}

// Отдельный подкомпонент, чтобы достать актуальные значения формы через useWatch
// и посчитать «Создать (N)» прямо на кнопке.
const SubmitRow: FC<{ isLoading: boolean }> = ({ isLoading }) => {
  const queryType = useWatch({ name: "queryType" }) as
    | OSINT.PayloadType
    | undefined
  const bulkInput = useWatch({ name: "bulkInput" }) as string | undefined
  const parsed = parseBulkInput(bulkInput ?? "", queryType)
  const canSubmit = parsed.payloads.length > 0 && !isLoading

  return (
    <Box display="flex" justifyContent="end" gap={1}>
      <Button type="submit" variant="contained" disabled={!canSubmit}>
        Создать ({parsed.payloads.length})&nbsp;
        {isLoading && <CircularProgress size={20} />}
      </Button>
    </Box>
  )
}
